import { Component, EventEmitter, HostBinding, Injector, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SiteInfo } from '@shared/services/api/access-sites.interface';
import { User } from '@app/layout/admin/services/api/user-interfaces';
import { SiteService, EscalatedTicket, Ticket, TicketsService } from '@shared/services';
import { EquipmentService } from '@shared/services/api/equipment.service';
import { AceLoggerService } from '@logging/ace-logger.service';
import { EscalatedTicketUpsertModalComponent } from '@app/layout/sites/tickets/escalated-ticket-upsert-modal/escalated-ticket-upsert-modal.component';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TicketModal} from '@app/layout/sites/tickets/ticket-modals/ticket-modal-util';
import { TicketOperationResult } from '@app/layout/sites/tickets/tickets.interface';
import {
  EscalatedTicketDetailsModalComponent,
  EscalatedTicketDetailsModal
} from '@app/layout/sites/tickets/escalated-ticket-details-modal/escalated-ticket-details-modal.component';
import { AceLogger } from '@logging/ace-logger';
import { TicketDataCaches } from '@shared/data-stores/ticket-data-caches';
import { TicketUtils } from '@app/layout/sites/tickets/ticket-utils';
import { I18nMessage } from '@app/shared/api/model/i18n';


@Component({
  selector: 'app-escalated-ticket-create-button',
  templateUrl: './escalated-ticket-create-button.component.html',
  styleUrls: ['./escalated-ticket-create-button.component.scss'],
  standalone: false
})
export class EscalatedTicketCreateButtonComponent implements OnChanges {

  private readonly logger: AceLogger;
  readonly defaultButtonClass = 'btn btn-success btn-sm';
  @Input() icon: string = 'tools';
  @Input() text: I18nMessage = {messageKey: 'Action'};
  @Input() site: SiteInfo | null;
  @Input() ticketTemplate: Partial<EscalatedTicket>;
  @Input() assigneesById: Map<string, User>;
  @Input() disabled = false;
  @Input() buttonClass: string = this.defaultButtonClass;
  /** bootstrap's `btn-link:disabled` applies `pointer-events:none;` which prevents title from showing on hover.
   * Applied to host instead which wraps the button area. */
  @HostBinding('attr.title')
  @Input() tooltipMessage: string;
  @Input() showSiteName: boolean = null;

  @Output() setLoading: EventEmitter<boolean> = new EventEmitter();
  @Output() assigneesByIdLoaded: EventEmitter<Map<string, User>> = new EventEmitter();
  @Output() ticketModalClosed: EventEmitter<EscalatedTicket> = new EventEmitter();
  @Output() ticketCreated: EventEmitter<EscalatedTicket> = new EventEmitter();

  private ticketModal: TicketModal<EscalatedTicket>;
  private ticketModalInjector: Injector;
  loading = false;

  constructor(
      private siteService: SiteService,
      private equipmentService: EquipmentService,
      private ticketsService: TicketsService,
      private modalService: NgbModal,
      private ticketDataCaches: TicketDataCaches,
      private injector: Injector,
      logService: AceLoggerService
  ) {
    this.logger = logService.create(EscalatedTicketCreateButtonComponent.name);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.site && this.assigneesById) {
      this.assigneesById = null;
    }
  }

  createTicket(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const modalRef = EscalatedTicketUpsertModalComponent.openInModal(
      this.modalService,
      {
        ...this.site && { site: this.site },
        ...this.ticketTemplate && { ticket: this.ticketTemplate }
      },
      { injector: this.getTicketModalInjector() });
    this.ticketModal = modalRef.componentInstance;
    modalRef.result
      .then(
          (result: TicketOperationResult<EscalatedTicket>) => {
            const ticket = result?.ticket;
            if (ticket) {
              this.ticketCreateFinished(<EscalatedTicket>ticket);
            }
          },
          // only set ticketModal to null when upsert is cancelled
          // if upsert proceeds ticketModal will be set to ticketDetailsModal
          (_reason) => this.ticketModal = null);
  }

  private getTicketModalInjector(): Injector {
    if (!this.ticketModalInjector) {
      this.ticketModalInjector = TicketUtils.makeTicketDataCachesInjector(this.ticketDataCaches, this.injector);
    }
    return this.ticketModalInjector;
  }

  private displayAction(action: EscalatedTicket) {
    this.siteService.getCachedSiteInfo(action.site.site_id).subscribe(site => {
      const modalInput: EscalatedTicketDetailsModal = {
        ticket: action,
        site,
        showOtherEscalatedTicketsCount: true,
        showOtherEscalatedTicketsCountAsLink: true,
        ...(this.showSiteName != null && {showSiteName: this.showSiteName})
      };
      const modalOptions: NgbModalOptions = {
        keyboard: false,
        injector: this.getTicketModalInjector()
      };
      const modalRef = EscalatedTicketDetailsModalComponent.openInModal(this.modalService, modalInput, modalOptions);
      this.ticketModal = modalRef.componentInstance;

      modalRef.result
        .then(
          (result: TicketOperationResult<Ticket>) => this.ticketDetailsClosed(<EscalatedTicket>result.ticket),
          (_reason) => this.ticketDetailsClosed(action));
    });
  }

  private ticketCreateFinished(ticket: EscalatedTicket) {
    this.ticketCreated.emit(<EscalatedTicket>ticket);
    this.displayAction(ticket);
  }

  private ticketDetailsClosed(ticket: EscalatedTicket) {
    this.ticketModalClosed.emit(ticket);
    this.ticketModal = null;
  }
}
